import React, { useState } from 'react';
import moment from 'moment';
import { validate as isValidUUID } from 'uuid';
import { DatePicker } from '@fortress-technology-solutions/fortress-component-library/Organisms';
import {
  NumericInput,
  Typography,
  IconButton,
  Tooltip,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import {
  MoneyIcon,
  TrashIcon,
  AlertInfoIcon,
} from '@fortress-technology-solutions/fortress-component-library/Icons';
import { Grid } from '@fortress-technology-solutions/fortress-component-library/Molecules';
import { formatDateDB } from '@fortress-technology-solutions/fortress-component-library/utils';
import useIntl from '../../../../../hooks/useIntl';
import messages from './messages';

const RentStep = ({
  id,
  idx,
  initialStartDate,
  initialEndDate,
  initialAmount,
  onChange,
  onDelete,
  priorRentStep,
  errors,
  leaseEndDate,
  leaseStartDate,
  renewalLeaseStartDate,
  renewalOngoing,
  canDelete,
  tooltipMessage,
  saving,
}) => {
  const [startDate, setStartDate] = useState(initialStartDate);
  const [amount, setAmount] = useState(initialAmount);
  const [showDisabledDateMessage, setShowDisabledDateMessage] = useState(false);
  const intl = useIntl();
  const disabledDateMessage = intl.formatMessage(
    messages.disabledDateErrorMessage,
  );

  const isFirstRentStep = idx === 0;
  const disableDateEdit =
    !!startDate && leaseStartDate.isAfter(moment(startDate));
  const rentStepIsSaved = isValidUUID(id) || idx === 0;
  const deletableRentStep = canDelete || !rentStepIsSaved;

  function shouldDisableDate(date) {
    const priorRentStepStartDate = moment(priorRentStep.startDate);

    const dateIsOnOrBeforePreviousStepStartDate = date.isSameOrBefore(
      priorRentStepStartDate,
    );
    const dateIsAfterCurrentLeaseEndDate = date.isSameOrAfter(leaseEndDate);
    const dateIsBeforeRenewalLeaseStartDate = date.isBefore(
      renewalLeaseStartDate,
    );

    return (
      saving ||
      dateIsOnOrBeforePreviousStepStartDate ||
      (renewalOngoing &&
        dateIsAfterCurrentLeaseEndDate &&
        !dateIsBeforeRenewalLeaseStartDate)
    );
  }
  function handleChange(name, value) {
    if (name === 'date') {
      const valueIsValid = moment(value).isValid();
      const isDisabledDate = shouldDisableDate(value);

      if (!valueIsValid || isDisabledDate) {
        setShowDisabledDateMessage(true);
        setStartDate(null);
        return;
      }
      setShowDisabledDateMessage(false);
      const formattedValue = formatDateDB(value, false);
      setStartDate(formattedValue);
      onChange({ id, startDate: formattedValue, amount, name, idx });
    } else {
      setAmount(value);
      onChange({ id, startDate, amount: value, name, idx });
    }
  }

  const hasErrors =
    errors?.startDate || errors?.amount || showDisabledDateMessage;

  const parsedAmount = disableDateEdit
    ? parseFloat(amount)?.toFixed(2)
    : amount;
  return (
    <>
      <Grid item xs={4}>
        {isFirstRentStep || rentStepIsSaved ? (
          <>
            <Typography
              variant="bodySmall"
              sx={{
                display: 'flex',
                alignItems: 'center',
                height: '100%',
              }}
            >
              {formatDateDB(initialStartDate)}
              <Tooltip
                title={tooltipMessage}
                arrow
                placement="top"
                sx={{
                  display: 'flex',
                  marginLeft: '2 px',
                  marginRight: '5 px',
                  verticalAlign: 'middle',
                }}
                variant="default"
                hidden={disableDateEdit}
              >
                <AlertInfoIcon
                  sx={{ marginLeft: 0.5, marginTop: 0.5 }}
                  fontSize={'small'}
                  strokeWidth={0.5}
                />
              </Tooltip>
            </Typography>
          </>
        ) : (
          <DatePicker
            disabled={disableDateEdit && !hasErrors}
            value={startDate}
            format="YYYY-MM-DD"
            placeholder="YYYY-MM-DD"
            shouldDisableDate={shouldDisableDate}
            onChange={(date) => handleChange('date', date)}
            error={hasErrors}
            helperText={
              errors?.startDate || showDisabledDateMessage
                ? disabledDateMessage
                : null
            }
          />
        )}
      </Grid>

      <Grid item xs={3}>
        <Typography
          variant="bodySmall"
          sx={{
            display: 'flex',
            alignItems: hasErrors ? 'flex-start' : 'center',
            paddingTop: hasErrors ? 1.5 : 0,
            height: '100%',
          }}
        >
          {!initialEndDate ? '---' : formatDateDB(initialEndDate)}
        </Typography>
      </Grid>
      <Grid item xs={4}>
        <NumericInput
          adornment={<MoneyIcon />}
          value={parsedAmount}
          disabled={disableDateEdit || saving || !startDate || rentStepIsSaved}
          onChange={({ target: { value } }) => {
            handleChange('amount', value);
          }}
          error={hasErrors}
          helperText={errors?.amount || null}
          step="0.01"
        />
      </Grid>
      <Grid item xs={1} sx={{ display: 'flex', alignItems: 'center' }}>
        {!disableDateEdit ? (
          <IconButton
            edge="start"
            variant={'delete'}
            disabled={saving || !deletableRentStep}
            onClick={() => onDelete({ id, idx })}
          >
            <TrashIcon />
          </IconButton>
        ) : null}
      </Grid>
    </>
  );
};

export default RentStep;
